import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getNeededDateFormat } from '../../../helper'
import { createTseNullOrderThunk, getTseNullOrdersThunk, printYearOrderThunk } from '../../../redux/thunk/tse'
import Button from '../../Elements/buttons/Button'
import { InfoItem, InfoItemData, InfoItemTitle, InfoWrapper, TseInformationButtonsWrap, TseInformationModalWrapper } from './index.styled'
import { format, subYears } from 'date-fns'
import NullOrderModal from './NullOrderModal'
import CustomFullScreenModal from '../../Elements/modal/CustomFullScreenModal'
import { setIsProcessingPrintYearOrder } from '../../../redux/action/tse'
import { setAppModal } from '../../../redux/action/system'

const EVENT_Z_INDEX = 996

const TseInformationModal = React.memo(() => {
	const { t } = useTranslation()
	const isOpen = useSelector(store => store.system.modal.tseInformationModalShow)
	const tseConfig = useSelector(store => store.tse.config)
	const [showNullOrderData, setShowNullOrderData] = useState(false)
	const dispatch = useDispatch()

	const tssId = tseConfig?.tssId
	const creationDate = tseConfig?.activationDate
	const creationDateToTimeFormat = getNeededDateFormat(creationDate, 'dd.MM.y, HH:mm:ss')
	const serialNumber = tseConfig?.serialNumber
	const certificate = tseConfig?.certificate
	const cashRegisterId = tseConfig?.cashRegisterId
	const cashRegisterVersion = tseConfig?.cashRegisterVersion
	const electronicRecordingSystemType = tseConfig?.electronicRecordingSystemType
	const bsiTseId = tseConfig?.bsiTseId
	const companyActivationDate = tseConfig?.companyActivationDate
	const companyActivationDateFormat = getNeededDateFormat(companyActivationDate, 'dd.MM.y, HH:mm:ss')
	const cashRegisterSerialNumber = tseConfig?.cashRegisterSerialNumber
	const cashRegisterBrand = tseConfig?.cashRegisterBrand
	const cashRegisterModel = tseConfig?.cashRegisterModel
	const tseFormFactor = tseConfig?.tseFormFactor
	const isAustriaTseType = tseConfig?.type === 'AUT'

	useEffect(() => {
		if (isOpen && showNullOrderData) {
			dispatch(getTseNullOrdersThunk())
		}
	}, [showNullOrderData]) // eslint-disable-line react-hooks/exhaustive-deps

	const handlePrint = useCallback(() => {
		if (showNullOrderData) {
			const date = new Date().toISOString()
			dispatch(createTseNullOrderThunk(date))
		} else {
			window.print()
		}
	}, [dispatch, showNullOrderData])

	const handleShowNullOrderData = useCallback(() => {
		setShowNullOrderData(true)
	}, [])

	const handleClose = useCallback(() => {
		if (showNullOrderData) {
			setShowNullOrderData(false)
		} else {
			dispatch(setAppModal({ tseInformationModalShow: false }))
		}
	}, [dispatch, showNullOrderData])

	const handleYearOrderPrint = useCallback(() => {
		dispatch(setIsProcessingPrintYearOrder(true))
		const lastYear = format(subYears(new Date(), 1), 'yyyy')
		dispatch(printYearOrderThunk(lastYear))
	}, [dispatch])

	return (
		<>
			<CustomFullScreenModal
				isOpen={isOpen && !showNullOrderData}
				close={handleClose}
				zIndex={EVENT_Z_INDEX}
				title={t('Modal.tse_modal.information.title')}
				disableBackButton={true}
				id="tse-information-modal"
				mobileFooter={true}
				footerClassname="big-gap mobile-column"
				button={
					<>
						<TseInformationButtonsWrap>
							{isOpen &&
								<Button name="send" icon="print" text={t('Modal.tse_modal.information.print')} keyButton="F2" zIndex={EVENT_Z_INDEX} color="green" onClick={handlePrint} className="back no-print" />}
						</TseInformationButtonsWrap>
						{isAustriaTseType &&
							<Button name="null-order" text={t('Modal.tse_modal.information.nullOrder')} keyButton="F3" zIndex={EVENT_Z_INDEX} onClick={handleShowNullOrderData} className="back no-print" />}
						<Button icon="checkmark" text={t('buttons.controls.ready')} color="green" onClick={handleClose} className="no-print" />
					</>
				}>
				<TseInformationModalWrapper>
					<InfoWrapper>
						{tssId && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.tseId')}</InfoItemTitle>
							<InfoItemData>{tssId}</InfoItemData>
						</InfoItem>}
						{electronicRecordingSystemType && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.electronicRecordingSystemType')}</InfoItemTitle>
							<InfoItemData>{electronicRecordingSystemType}</InfoItemData>
						</InfoItem>}
						{serialNumber && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.serialNumber')}</InfoItemTitle>
							<InfoItemData>{serialNumber}</InfoItemData>
						</InfoItem>}
						{bsiTseId && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.bsiTseId')}</InfoItemTitle>
							<InfoItemData>{bsiTseId}</InfoItemData>
						</InfoItem>}
						{companyActivationDate && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.companyActivationDate')}:</InfoItemTitle>
							<InfoItemData>{companyActivationDateFormat}</InfoItemData>
						</InfoItem>}
						{creationDate && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.createDate')}</InfoItemTitle>
							<InfoItemData>{creationDateToTimeFormat}</InfoItemData>
						</InfoItem>}
						{certificate && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.certificate')}</InfoItemTitle>
							<InfoItemData className="certificate">{certificate}</InfoItemData>
						</InfoItem>}
						{tseFormFactor && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.tseFormFactor')}</InfoItemTitle>
							<InfoItemData>{tseFormFactor}</InfoItemData>
						</InfoItem>}
						{cashRegisterId && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.cashDeskId')}</InfoItemTitle>
							<InfoItemData>{cashRegisterId}</InfoItemData>
						</InfoItem>}
						{cashRegisterVersion && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.cashDeskVersion')}</InfoItemTitle>
							<InfoItemData>{cashRegisterVersion}</InfoItemData>
						</InfoItem>}
						{cashRegisterSerialNumber && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.cashRegisterSerialNumber')}:</InfoItemTitle>
							<InfoItemData>{cashRegisterSerialNumber}</InfoItemData>
						</InfoItem>}
						{cashRegisterBrand && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.cashRegisterBrand')}:</InfoItemTitle>
							<InfoItemData>{cashRegisterBrand}</InfoItemData>
						</InfoItem>}
						{cashRegisterModel && <InfoItem>
							<InfoItemTitle>{t('Modal.tse_modal.information.cashRegisterModel')}:</InfoItemTitle>
							<InfoItemData>{cashRegisterModel}</InfoItemData>
						</InfoItem>}
					</InfoWrapper>
				</TseInformationModalWrapper>
			</CustomFullScreenModal>

			<NullOrderModal {...{ isOpen: showNullOrderData, handlePrint, handleClose, handleYearOrderPrint }} />
		</>
	)
})

TseInformationModal.displayName = 'TseInformationModal'
export default TseInformationModal
